import { useCallback, useState } from "react";
import { RequestInitWithRetry } from "@lib/request";
import { useWorldRequest } from "@lib/useWorldRequest";
import { IFilterResult } from "@components/filters/filterLib";

export interface FilterDataFetcher<T = any> {
  (): (options: RequestInitWithRetry) => Promise<T>
}

export interface FilterDataProcessor<T extends any[] = any[]> {
  (data: T, hiddenFilters: string[]): IFilterResult
}


export interface UseGenericFilterOptionsParams<T extends any[] = any[], THidden = string> {
  hiddenFilters: THidden[],
  dataFetchers: FilterDataFetcher<T[number]>[],
  dataProcessor: FilterDataProcessor<T>,
  getInitialFilters: (hiddenFilters: THidden[]) => IFilterResult
}

export interface UseGenericFilterOptionsReturn {
  availableFilters: IFilterResult,
  filtersLoaded: boolean
}

export const useGenericFilterOptions = <T extends any[] = any[], THidden extends string = string>({
  hiddenFilters,
  dataFetchers,
  dataProcessor,
  getInitialFilters
}: UseGenericFilterOptionsParams<T, THidden>): UseGenericFilterOptionsReturn => {
  const [{ availableFilters, filtersLoaded }, setAvailableFilters] = useState({
    availableFilters: getInitialFilters(hiddenFilters),
    filtersLoaded: false
  });

  const onFiltersLoaded = useCallback((data: T) => {
    const processedFilters = dataProcessor(data, hiddenFilters as string[]);
    setAvailableFilters({
      availableFilters: processedFilters,
      filtersLoaded: true
    });
  }, [dataProcessor, hiddenFilters]);

  const filtersDataFetcher = useCallback(() => {
    if (filtersLoaded) {
      return;
    }
    return (options: RequestInitWithRetry) => {
      return Promise.all(
        dataFetchers.map(fetcher => fetcher()(options))
      ) as Promise<T>;
    };
  }, [filtersLoaded, dataFetchers]);

  useWorldRequest(filtersDataFetcher, {
    initialData: dataFetchers.map((): any => undefined) as T,
    onSuccess: onFiltersLoaded
  });

  return {
    availableFilters,
    filtersLoaded
  };
};
